import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ConfiguratorI18nCacheService, ConfiguratorStructureService, DatastoreDto, Partner, TableLiteDto, ImportZipBody, UserDTO } from '@tj/tj-dmp-lib-api';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

@Component({
    selector: 'dmp-tables',
    templateUrl: './tables.component.html',
    styleUrls: ['./tables.component.scss'],
    providers: [ConfirmationService, MessageService],
    standalone: false
})
export class TablesComponent implements OnInit {
    breadcrumb = [
        { label: this.translate.instant('menu.data-center.label') },
        { label: this.translate.instant('menu.data-center.tables') }
    ];
    description = this.translate.instant('tablesPage.description');
    tables: TableLiteDto[];
    selectedTables: TableLiteDto[] = [];
    editable = null;
    /** Description d'origine de la ligne en cours d'édition, pour pouvoir annuler. */
    private descriptionBackup: string = null;
    datastore: DatastoreDto;
    currentPartner: Partner | null = null;
    exportMenuItems = [];
    private actionItemsByTable: Record<string, MenuItem[]> = {};

    dialogAddTable = false;
    formAddTable: UntypedFormGroup;

    constructor(
        private structureService: ConfiguratorStructureService,
        private router: Router,
        private confirmationService: ConfirmationService,
        private translate: TranslateService,
        private conf: ConfiguratorI18nCacheService,
        private readonly formBuilder: UntypedFormBuilder,
        private messageService: MessageService
    ) {
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }

    private rebuildTranslatedHeader(): void {
        this.breadcrumb = [
            { label: this.translate.instant('menu.data-center.label') },
            { label: this.translate.instant('menu.data-center.tables') }
        ];
        this.description = this.translate.instant('tablesPage.description');
    }

    ngOnInit(): void {
        this.initExportMenu();
    }

    datastoreChange(datastore: DatastoreDto) {
        this.datastore = datastore;
        this.updateListTable();
    }

    partnerChange(partner: Partner | Partner[]) {
        this.currentPartner = Array.isArray(partner) ? (partner[0] ?? null) : (partner ?? null);
    }

    private updateListTable() {
        if (this.datastore) {
            this.structureService.getAllTablesUsingGET(this.datastore.code)
                .subscribe((res) => {
                    this.tables = res;
                    this.actionItemsByTable = {};
                    this.selectedTables = [];
                    this.updateExportMenuState();
                });
        } else {
            this.tables = [];
            this.actionItemsByTable = {};
            this.selectedTables = [];
            this.updateExportMenuState();
        }
    }

    doSaveDescription(table: TableLiteDto) {
        this.structureService.updateTableDescriptionUsingPUT(this.datastore.code, table.description, table.code, undefined, this.currentPartner?.code)
            .subscribe(res => {
                this.editable = null;
                this.descriptionBackup = null;
            });

    }

    cancelEditDescription(): void {
        this.restoreDescription();
        this.editable = null;
    }

    /** Remet la description d'origine sur la ligne en cours d'édition (modifications non enregistrées). */
    private restoreDescription(): void {
        if (this.editable) {
            const table = this.tables?.find(t => t.code === this.editable);
            if (table) {
                table.description = this.descriptionBackup;
            }
        }
        this.descriptionBackup = null;
    }

    createTable() {
        this.formAddTable = this.formBuilder.group({
            code: ['', [Validators.pattern('[-_a-z0-9]*'), Validators.required, Validators.minLength(2), Validators.maxLength(10)]],
            ownerPartner: [this.currentPartner?.code ?? ''],
        });
        this.dialogAddTable = true;
    }

    doAddTable() {
        if (!this.formAddTable.invalid) {
            const ownerPartner = this.formAddTable.value.ownerPartner;
            const dto: TableLiteDto = { code: this.formAddTable.value.code };

            this.structureService
                .createTableUsingPOST(this.datastore.code, dto, ownerPartner || undefined)
                .subscribe(res => {
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('tablesPage.addedDetail')
                    });
                    this.updateListTable();
                });
            this.dialogAddTable = false;
        }
    }

    delete(table: string, $event: any) {
        this.confirmationService.confirm({
            target: $event?.target as HTMLElement,
            message: this.translate.instant('tablesPage.confirmDeleteMessage', {name: table}),
            accept: () => {
                this.structureService.deleteTableUsingDELETE(this.datastore.code, table, undefined, this.currentPartner?.code).subscribe(() => {
                    this.updateListTable();
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('tablesPage.deletedDetail')
                    });
                }, () => {
                    this.updateListTable();
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: this.translate.instant('landingPage.template.genericErrorDetail')
                    });
                });
            }
        });
    }

    private exportConfig(name: string): void {
        this.structureService.exportJson(name, this.datastore.code).subscribe(
            (response) => {
                const a = document.createElement('a');
                a.href = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(response, null, 2));
                a.download = name + '.json';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                this.messageService.add({ severity: 'success', summary: this.translate.instant('etl.list.exportSuccessSummary') });
            }, error => {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('etl.list.exportErrorSummary') });
            });
    }

    @ViewChild('fileInput')
    fileInput: ElementRef<HTMLInputElement>;
    triggerFileUpload(): void {
        this.fileInput.nativeElement.click();
    }

    importJson(event: any) {
        const jsonFiles: FileList = event.target.files;
        if (jsonFiles.length === 0) {
            this.messageService.add({ severity: 'warn', summary: this.translate.instant('etl.list.noFileSelectedSummary') });
            return;
        }

        Array.from(jsonFiles).forEach((file: File) => {
            const reader: FileReader = new FileReader();
            reader.onload = (e: any) => {
                if (file.type === 'application/json') {
                    try {
                        const jsonContent: string = JSON.parse(e.target.result);
                        this.handleJsonImport(jsonContent);
                    } catch (error) {
                        this.messageService.add({ severity: 'error', summary: this.translate.instant('etl.list.jsonParseErrorSummary') });
                        this.resetFileInput();
                    }
                } else if (file.type === 'application/zip') {
                    this.handleZipImport(file);
                } else {
                    this.messageService.add({ severity: 'error', summary: this.translate.instant('etl.list.unsupportedFormatSummary') });
                    this.resetFileInput();
                }
            };
            reader.readAsText(file);
        });
    }
    resetFileInput(): void {
        this.fileInput.nativeElement.value = '';
    }
    handleJsonImport(jsonContent: string): void {
        this.structureService.importFromJson(jsonContent, this.datastore.code).subscribe(
            () => {
                this.updateListTable();
                this.messageService.add({ severity: 'success', summary: this.translate.instant('etl.list.importSuccessSummary') });
                this.resetFileInput();
            }, error => {
                this.resetFileInput();
            }
        );
    }
    handleZipImport(zipFile: File): void {
        const formData: FormData = new FormData();
        formData.append('file', zipFile);
        this.structureService.importFromZip(this.datastore.code, formData as unknown as ImportZipBody).subscribe(
            () => {
                this.updateListTable();
                this.messageService.add({ severity: 'success', summary: this.translate.instant('etl.list.importSuccessSummary') });
                this.resetFileInput();
            }, error => {
                this.resetFileInput();
            }
        );
    }

    private exportZip() {
        if (this.tables.length > 0) {
            this.handleZipExport(this.tables.map((etl) => etl.code));
        } else {
            this.messageService.add({ severity: 'warn', summary: this.translate.instant('tablesPage.noJobToExportSummary') });
        }
    }

    private handleZipExport(codes: string[]) {
        this.structureService.exportZip(codes, this.datastore.code).subscribe(
            (response) => {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(new Blob([response], { type: 'application/zip' }));
                a.download = 'export.zip';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                this.messageService.add({ severity: 'success', summary: this.translate.instant('etl.list.exportSuccessSummary') });
            }, error => {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('etl.list.exportErrorSummary') });
            }
        );
    }

    // ============ EXPORT/IMPORT MENU HELPERS ============

    private initExportMenu() {
        this.exportMenuItems = [
            {
                label: this.translate.instant('subscriptions.importFile'),
                icon: 'fas fa-upload',
                permission: 'TABLES.import',
                command: () => {
                    this.triggerFileUpload();
                }
            },
            {
                label: this.translate.instant('subscriptions.exportSelection'),
                icon: 'fas fa-file-export',
                permission: 'TABLES.export',
                command: () => {
                    this.exportSelected();
                },
                disabled: !this.selectedTables || this.selectedTables.length === 0
            },
            {
                label: this.translate.instant('subscriptions.exportAll'),
                icon: 'fas fa-download',
                permission: 'TABLES.export',
                command: () => {
                    this.exportZip();
                },
                disabled: !this.tables || this.tables.length === 0
            }
        ];
    }

    onSelectionChange() {
        this.updateExportMenuState();
    }

    private updateExportMenuState() {
        if (!this.exportMenuItems || this.exportMenuItems.length < 3) {
            return;
        }
        // Update "Exporter la sélection" (index 1)
        this.exportMenuItems[1].disabled = !this.selectedTables || this.selectedTables.length === 0;
        // Update "Exporter tout" (index 2)
        this.exportMenuItems[2].disabled = !this.tables || this.tables.length === 0;
    }

    private exportSelected() {
        if (!this.selectedTables || this.selectedTables.length === 0) {
            this.messageService.add({ severity: 'warn', summary: this.translate.instant('tablesPage.noTableSelectedSummary') });
            return;
        }
        const codes = this.selectedTables.map(t => t.code);
        this.handleZipExport(codes);
    }

    getDescriptionInputId(tableCode: string): string {
        return `table-description-input-${tableCode.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
    }

    private enableDescriptionEdition(tableCode: string): void {
        this.restoreDescription();
        this.editable = tableCode;
        this.descriptionBackup = this.tables?.find(t => t.code === tableCode)?.description ?? null;
        setTimeout(() => {
            const input = document.getElementById(this.getDescriptionInputId(tableCode)) as HTMLInputElement | null;
            input?.focus();
            input?.select();
        });
    }

    getItemsListAction(tableCode: string): MenuItem[] {
        const cachedItems = this.actionItemsByTable[tableCode];
        if (cachedItems) {
            return cachedItems;
        }

        const items: MenuItem[] = [
            {
                label: this.translate.instant('tablesPage.editDescription'),
                icon: 'fas fa-pen',
                permission: 'TABLES.edit',
                command: () => {
                    if (this.editable && this.editable !== tableCode) {
                        this.confirmationService.confirm({
                            message: this.translate.instant('tablesPage.confirmEditOtherMessage'),
                            accept: () => {
                                this.enableDescriptionEdition(tableCode);
                            }
                        });
                    } else {
                        this.enableDescriptionEdition(tableCode);
                    }
                }
            },
            {
                label: this.translate.instant('subscriptions.delete'),
                icon: 'far fa-trash-alt',
                permission: 'TABLES.delete',
                command: (event) => {
                    this.delete(tableCode, event?.originalEvent ?? event);
                }
            },
            {
                label: this.translate.instant('etl.list.exportConfigAction'),
                icon: 'fas fa-file-export',
                permission: 'TABLES.export',
                command: () => {
                    this.exportConfig(tableCode);
                }
            },
            {
                separator: true
            },
            {
                label: this.translate.instant('viewEditor.list.consultData'),
                icon: 'fas fa-database',
                permission: 'TABLES.read',
                command: () => {
                    this.router.navigate(['/h/tables', this.datastore.code, tableCode, 'read']);
                }
            },
            {
                label: this.translate.instant('tablesPage.consultSchema'),
                icon: 'fas fa-table',
                permission: 'TABLES.read',
                command: () => {
                    this.router.navigate(['/h/tables', this.datastore.code, tableCode, 'view']);
                }
            }
        ];

        this.actionItemsByTable[tableCode] = items;
        return items;
    }
}
